import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  MessageSquare, Plus, Pencil, Trash2, Send, Video, Upload, Loader2,
  CheckCircle2, AlertTriangle, X, Eye, EyeOff, Sparkles, BookOpen,
  AlertCircle, GraduationCap, Film, Play, FileSpreadsheet,
} from "lucide-react";
import { Progress } from "./ui/progress";
import { Checkbox } from "./ui/checkbox";

const CATEGORIES = [
  { value: "boas-vindas", label: "Boas-vindas", icon: Sparkles, color: "emerald" },
  { value: "tutorial", label: "Tutorial", icon: BookOpen, color: "blue" },
  { value: "aviso", label: "Aviso", icon: AlertCircle, color: "amber" },
  { value: "treinamento", label: "Treinamento", icon: GraduationCap, color: "violet" },
];

// Pre-built admin upload flows that can be linked to a template, so
// right after the upload finishes the admin sees this template as a
// "ready-made message" option to fire via WhatsApp.
const DOC_TRIGGERS = [
  { value: "kit-admissional", label: "Kit Admissional" },
  { value: "aviso-ferias", label: "Aviso de Férias" },
  { value: "recibo-ferias", label: "Recibo de Férias" },
];

const colorClasses = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  violet: "bg-violet-100 text-violet-800 border-violet-200",
};

const findCategory = (value) =>
  CATEGORIES.find((c) => c.value === value) || CATEGORIES[2];

const AdminMessageTemplatesTab = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState(null);
  const [broadcastTarget, setBroadcastTarget] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/message-templates`, {
        headers, timeout: 15000,
      });
      setItems(r.data.items || []);
    } catch (err) {
      toast.error("Erro ao listar mensagens");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);

  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta mensagem? Essa ação é definitiva.")) return;
    try {
      await axios.delete(`${API}/admin/message-templates/${id}`, { headers });
      toast.success("Mensagem excluída");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(
        `${API}/admin/message-templates/${item.id}`,
        { active: !item.active },
        { headers },
      );
      fetchItems();
    } catch (err) {
      toast.error("Erro ao atualizar");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center font-heading text-lg">
                <MessageSquare className="w-5 h-5 mr-2 text-emerald-600" />
                Quadro de Mensagens
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Crie mensagens reutilizáveis (com ou sem vídeo) para disparar via
                WhatsApp ou exibir na tela de boas-vindas do funcionário.
              </CardDescription>
            </div>
            <Button
              onClick={() => { setEditorTarget(null); setEditorOpen(true); }}
              className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs"
              data-testid="message-templates-new-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Nova mensagem
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category filter */}
          <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
            <Button
              size="sm"
              variant={filter === "all" ? "default" : "outline"}
              onClick={() => setFilter("all")}
              className="h-8 text-xs flex-shrink-0"
              data-testid="message-filter-all"
            >
              Todas ({items.length})
            </Button>
            {CATEGORIES.map((c) => {
              const count = items.filter((i) => i.category === c.value).length;
              const Icon = c.icon;
              return (
                <Button
                  key={c.value}
                  size="sm"
                  variant={filter === c.value ? "default" : "outline"}
                  onClick={() => setFilter(c.value)}
                  className="h-8 text-xs flex-shrink-0"
                  data-testid={`message-filter-${c.value}`}
                >
                  <Icon className="w-3.5 h-3.5 mr-1" />
                  {c.label} ({count})
                </Button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              Nenhuma mensagem cadastrada nesta categoria.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AnimatePresence>
                {visible.map((item) => (
                  <MessageCard
                    key={item.id}
                    item={item}
                    onEdit={() => { setEditorTarget(item); setEditorOpen(true); }}
                    onDelete={() => handleDelete(item.id)}
                    onBroadcast={() => setBroadcastTarget(item)}
                    onToggleActive={() => handleToggleActive(item)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>

      <MessageEditor
        open={editorOpen}
        onOpenChange={(o) => { if (!o) setEditorTarget(null); setEditorOpen(o); }}
        target={editorTarget}
        onSaved={fetchItems}
      />

      <BroadcastTemplateDialog
        target={broadcastTarget}
        onOpenChange={(o) => { if (!o) setBroadcastTarget(null); }}
      />
    </div>
  );
};

const MessageCard = ({ item, onEdit, onDelete, onBroadcast, onToggleActive }) => {
  const cat = findCategory(item.category);
  const Icon = cat.icon;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      className={`p-4 rounded-lg border ${item.active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/50"} shadow-sm hover:shadow-md transition`}
      data-testid={`message-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className={`${colorClasses[cat.color]} text-[10px]`}>
            <Icon className="w-3 h-3 mr-1" />
            {cat.label}
          </Badge>
          {item.video_id && (
            <Badge variant="outline" className="bg-slate-900 text-white border-slate-900 text-[10px]">
              <Film className="w-3 h-3 mr-1" />
              vídeo
            </Badge>
          )}
          {!item.active && (
            <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">
              inativa
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={onToggleActive}
            className="h-7 w-7"
            title={item.active ? "Desativar" : "Ativar"}
            data-testid={`message-toggle-${item.id}`}
          >
            {item.active ? (
              <Eye className="w-3.5 h-3.5 text-slate-500" />
            ) : (
              <EyeOff className="w-3.5 h-3.5 text-slate-400" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onEdit}
            className="h-7 w-7"
            data-testid={`message-edit-${item.id}`}
          >
            <Pencil className="w-3.5 h-3.5 text-slate-500" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onDelete}
            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`message-delete-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <h4 className="text-sm font-semibold text-slate-900 line-clamp-1 mb-1">
        {item.title}
      </h4>
      <p className="text-xs text-slate-600 line-clamp-3 mb-3 whitespace-pre-wrap">
        {item.body}
      </p>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-400 flex items-center gap-1.5">
          {item.show_in_app ? (
            <>
              <Eye className="w-3 h-3" /> Aparece no app
            </>
          ) : (
            <>
              <EyeOff className="w-3 h-3" /> Oculta no app
            </>
          )}
        </span>
        <Button
          size="sm"
          className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
          onClick={onBroadcast}
          disabled={!item.active}
          data-testid={`message-broadcast-${item.id}`}
        >
          <Send className="w-3 h-3 mr-1" />
          Disparar
        </Button>
      </div>
    </motion.div>
  );
};

const MessageEditor = ({ open, onOpenChange, target, onSaved }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("aviso");
  const [showInApp, setShowInApp] = useState(true);
  const [active, setActive] = useState(true);
  const [docTriggers, setDocTriggers] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const [videoFile, setVideoFile] = useState(null);
  const [existingVideoId, setExistingVideoId] = useState(null);
  const [existingVideoName, setExistingVideoName] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setTitle(target.title || "");
      setBody(target.body || "");
      setCategory(target.category || "aviso");
      setShowInApp(target.show_in_app !== false);
      setActive(target.active !== false);
      setExistingVideoId(target.video_id || null);
      setExistingVideoName(target.video_filename || null);
      setDocTriggers(new Set(target.document_triggers || []));
    } else {
      setTitle("");
      setBody("");
      setCategory("aviso");
      setShowInApp(true);
      setActive(true);
      setExistingVideoId(null);
      setExistingVideoName(null);
      setDocTriggers(new Set());
    }
    setVideoFile(null);
    setUploadProgress(0);
  }, [open, target]);

  const handleSave = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Título e mensagem são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      let templateId = target?.id;
      const payload = {
        title: title.trim(),
        body: body.trim(),
        category,
        show_in_app: showInApp,
        active,
        document_triggers: Array.from(docTriggers),
      };
      if (target) {
        await axios.put(
          `${API}/admin/message-templates/${target.id}`,
          payload,
          { headers },
        );
      } else {
        const r = await axios.post(
          `${API}/admin/message-templates`,
          payload,
          { headers },
        );
        templateId = r.data.id;
      }

      // Upload new video if user attached one
      if (videoFile && templateId) {
        setUploadingVideo(true);
        const fd = new FormData();
        fd.append("file", videoFile);
        await axios.post(
          `${API}/admin/message-templates/${templateId}/video`,
          fd,
          {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
              if (e.total) {
                setUploadProgress(Math.round((e.loaded * 100) / e.total));
              }
            },
            timeout: 300000,
          },
        );
        setUploadingVideo(false);
      }

      toast.success(target ? "Mensagem atualizada" : "Mensagem criada");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = async () => {
    if (!target?.id || !existingVideoId) {
      setVideoFile(null);
      return;
    }
    if (!window.confirm("Remover o vídeo atual desta mensagem?")) return;
    try {
      await axios.delete(
        `${API}/admin/message-templates/${target.id}/video`,
        { headers },
      );
      setExistingVideoId(null);
      setExistingVideoName(null);
      setVideoFile(null);
      toast.success("Vídeo removido");
      onSaved?.();
    } catch (err) {
      toast.error("Erro ao remover vídeo");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="message-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            {target ? "Editar mensagem" : "Nova mensagem"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Defina o título, categoria e o texto. Opcionalmente anexe um vídeo de até 25 MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Category */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5 block">
              Categoria
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const selected = category === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    className={`p-2 rounded-md text-xs border transition flex flex-col items-center gap-1 ${
                      selected
                        ? `${colorClasses[c.color]} border-2`
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                    data-testid={`editor-category-${c.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 block">
              Título
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Ex: Bem-vindo ao app da AVI"
              className="h-9 text-sm"
              data-testid="editor-title"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 block">
              Mensagem
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              placeholder="Escreva a mensagem completa. Você pode usar quebras de linha. Quando disparada via WhatsApp, será exibida com o título em negrito antes dela."
              className="w-full h-32 p-2.5 text-sm border border-slate-200 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="editor-body"
            />
            <p className="text-[10px] text-slate-400 text-right">{body.length}/2000</p>
          </div>

          {/* Video */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 block">
              Vídeo (opcional)
            </label>
            {existingVideoId && !videoFile && (
              <div className="flex items-center justify-between p-2.5 bg-slate-900 text-white rounded-md mb-2">
                <div className="flex items-center gap-2 text-xs">
                  <Film className="w-4 h-4 text-emerald-400" />
                  <span className="truncate max-w-[200px]">{existingVideoName || "video.mp4"}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRemoveVideo}
                  className="h-7 text-[11px] text-red-400 hover:text-red-300 hover:bg-red-950/40"
                  data-testid="editor-remove-video"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Remover
                </Button>
              </div>
            )}
            {videoFile && (
              <div className="flex items-center justify-between p-2.5 bg-emerald-50 border border-emerald-200 rounded-md mb-2">
                <div className="flex items-center gap-2 text-xs text-emerald-800">
                  <Video className="w-4 h-4" />
                  <span className="truncate max-w-[180px]">{videoFile.name}</span>
                  <span className="text-[10px] text-emerald-600">
                    {(videoFile.size / (1024 * 1024)).toFixed(1)} MB
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setVideoFile(null)}
                  className="h-7 text-[11px]"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 25 * 1024 * 1024) {
                  toast.error("Vídeo muito grande (máx 25 MB)");
                  return;
                }
                setVideoFile(f);
              }}
              data-testid="editor-video-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingVideo}
              className="h-9 text-xs w-full"
              data-testid="editor-pick-video"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {existingVideoId ? "Substituir vídeo" : "Adicionar vídeo"}
              <span className="ml-2 text-[10px] text-slate-400">.mp4 / até 25 MB</span>
            </Button>
            {uploadingVideo && uploadProgress > 0 && (
              <div className="mt-2 space-y-1">
                <Progress value={uploadProgress} className="h-1.5" />
                <p className="text-[10px] text-slate-500">Enviando: {uploadProgress}%</p>
              </div>
            )}
          </div>

          {/* Flags */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <label className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 text-xs">
              <Checkbox
                checked={showInApp}
                onCheckedChange={(v) => setShowInApp(v === true)}
                data-testid="editor-show-in-app"
              />
              <div>
                <p className="font-medium text-slate-800">Mostrar no app</p>
                <p className="text-[10px] text-slate-500">Tela de boas-vindas</p>
              </div>
            </label>
            <label className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 text-xs">
              <Checkbox
                checked={active}
                onCheckedChange={(v) => setActive(v === true)}
                data-testid="editor-active"
              />
              <div>
                <p className="font-medium text-slate-800">Ativa</p>
                <p className="text-[10px] text-slate-500">Disponível pra disparo</p>
              </div>
            </label>
          </div>

          {/* Document triggers — link this template to an upload flow */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5 block">
              Vincular como mensagem pronta para
            </label>
            <p className="text-[10px] text-slate-400 mb-1.5">
              Ao subir um destes documentos, o admin verá este template como opção.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
              {DOC_TRIGGERS.map((t) => {
                const selected = docTriggers.has(t.value);
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => {
                      setDocTriggers((prev) => {
                        const next = new Set(prev);
                        if (next.has(t.value)) next.delete(t.value);
                        else next.add(t.value);
                        return next;
                      });
                    }}
                    className={`p-2 rounded-md text-[11px] border transition flex items-center justify-center gap-1.5 ${
                      selected
                        ? "border-emerald-600 bg-emerald-50 text-emerald-800 border-2"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                    data-testid={`editor-trigger-${t.value}`}
                  >
                    {selected && <CheckCircle2 className="w-3 h-3" />}
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !title.trim() || !body.trim()}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="editor-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BroadcastTemplateDialog = ({ target, onOpenChange }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const open = !!target;

  const [eligible, setEligible] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [job, setJob] = useState(null);
  const [includeLogo, setIncludeLogo] = useState(true);
  const pollRef = useRef(null);
  const xlsxInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setJob(null);
      setSearch("");
      setImportReport(null);
      return;
    }
    axios
      .get(`${API}/admin/broadcast-whatsapp/eligible`, { headers, timeout: 15000 })
      .then((r) => setEligible(r.data.employees || []))
      .catch(() => setEligible([]));
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!job?.job_id || job.status === "done") {
      clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/broadcast-whatsapp/${job.job_id}`,
          { headers, timeout: 10000 },
        );
        setJob(r.data);
        if (r.data.status === "done") clearInterval(pollRef.current);
      } catch { /* transient */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [job?.job_id, job?.status]);

  if (!target) return null;

  const filtered = eligible.filter((e) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (e.nome || "").toLowerCase().includes(q) ||
      (e.matricula || "").toLowerCase().includes(q)
    );
  });

  const toggle = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSel = filtered.length > 0 && filtered.every((e) => selectedIds.has(e.id));
  const toggleAll = () => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (allSel) filtered.forEach((e) => next.delete(e.id));
      else filtered.forEach((e) => next.add(e.id));
      return next;
    });
  };

  const handleImportXlsx = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/admin/employees/match-from-xlsx`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      const matched = r.data?.matched || [];
      const notFound = r.data?.not_found || [];
      const eligibleSet = new Set(eligible.map((e) => e.id));
      const matchedEligible = matched.filter((m) => eligibleSet.has(m.id));
      const matchedIneligible = matched.filter((m) => !eligibleSet.has(m.id));

      setSelectedIds((prev) => {
        const next = new Set(prev);
        matchedEligible.forEach((m) => next.add(m.id));
        return next;
      });

      setImportReport({
        rows: r.data?.stats?.rows || 0,
        with_whatsapp: matchedEligible.length,
        without_whatsapp: matchedIneligible.length,
        not_found: notFound.length,
        not_found_items: notFound.slice(0, 50),
        ineligible_items: matchedIneligible.slice(0, 50),
      });

      if (matchedEligible.length) {
        toast.success(`${matchedEligible.length} funcionário(s) selecionado(s) da planilha`);
      } else if (matched.length) {
        toast.warning(`${matched.length} encontrado(s), mas nenhum com WhatsApp confirmado.`);
      } else {
        toast.error("Nenhum funcionário da planilha foi reconhecido.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao ler planilha.");
    } finally {
      setImporting(false);
      if (xlsxInputRef.current) xlsxInputRef.current.value = "";
    }
  };

  const fire = async () => {
    if (selectedIds.size === 0) {
      toast.error("Selecione pelo menos um destinatário");
      return;
    }
    if (!window.confirm(`Disparar "${target.title}" para ${selectedIds.size} funcionário(s)?`)) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/message-templates/${target.id}/broadcast`,
        {
          employee_ids: Array.from(selectedIds),
          include_logo: includeLogo,
        },
        { headers, timeout: 15000 },
      );
      toast.success(`Disparo iniciado para ${r.data.recipients}`);
      setJob({
        job_id: r.data.job_id,
        status: "queued",
        sent: 0, failed: 0,
        total: r.data.recipients,
        errors: [],
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao disparar");
    } finally {
      setSending(false);
    }
  };

  const isRunning = job && job.status !== "done";
  const isDone = job && job.status === "done";
  const progress = job && job.total
    ? Math.min(100, Math.round(((job.sent + job.failed) / job.total) * 100))
    : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!isRunning) onOpenChange(o); }}>
      <DialogContent className="max-w-xl max-h-[92vh] flex flex-col" data-testid="broadcast-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4 text-emerald-600" />
            Disparar mensagem
          </DialogTitle>
          <DialogDescription className="text-xs">
            <span className="font-semibold text-slate-800">"{target.title}"</span>
            {target.video_id && (
              <Badge variant="outline" className="ml-2 bg-slate-900 text-white border-slate-900 text-[10px]">
                <Film className="w-3 h-3 mr-1" />vídeo
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {!job && (
          <>
            <input
              ref={xlsxInputRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleImportXlsx(e.target.files?.[0])}
              data-testid="broadcast-tpl-xlsx-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => xlsxInputRef.current?.click()}
              disabled={importing}
              className="w-full h-9 text-xs justify-start border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 mb-2"
              data-testid="broadcast-tpl-xlsx-btn"
            >
              {importing
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-700" />}
              Importar destinatários por planilha (.xlsx)
            </Button>

            {importReport && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-2.5 rounded-md border border-slate-200 bg-slate-50 text-[11px] space-y-1 mb-2"
                data-testid="broadcast-tpl-import-report"
              >
                <div className="flex items-center justify-between font-medium text-slate-700">
                  <span>
                    <FileSpreadsheet className="inline w-3.5 h-3.5 mr-1 text-emerald-700" />
                    Resultado da planilha
                  </span>
                  <button type="button" onClick={() => setImportReport(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1 text-slate-600">
                  <span>📊 Linhas lidas:</span>
                  <span className="text-right font-mono">{importReport.rows}</span>
                  <span>✅ Selecionados (c/ WhatsApp):</span>
                  <span className="text-right font-mono text-emerald-700 font-semibold">{importReport.with_whatsapp}</span>
                  {importReport.without_whatsapp > 0 && (<>
                    <span>⏳ Sem WhatsApp confirmado:</span>
                    <span className="text-right font-mono text-amber-700">{importReport.without_whatsapp}</span>
                  </>)}
                  {importReport.not_found > 0 && (<>
                    <span>❌ Não encontrados:</span>
                    <span className="text-right font-mono text-red-700">{importReport.not_found}</span>
                  </>)}
                </div>
                {(importReport.not_found_items?.length > 0 || importReport.ineligible_items?.length > 0) && (
                  <details>
                    <summary className="cursor-pointer text-slate-500 mt-1">Ver detalhes</summary>
                    <ul className="mt-1 ml-4 list-disc max-h-32 overflow-y-auto space-y-0.5">
                      {importReport.not_found_items?.map((x, i) => (
                        <li key={`nf-${i}`} className="text-red-600">
                          Não encontrado: {x.nome_input || "—"} ({x.cpf_input || "sem CPF"})
                        </li>
                      ))}
                      {importReport.ineligible_items?.map((x, i) => (
                        <li key={`ie-${i}`} className="text-amber-700">
                          Sem WhatsApp: {x.nome} ({x.matricula})
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </motion.div>
            )}

            <div className="flex items-center gap-2 mb-2">
              <Input
                placeholder="Buscar por nome ou matrícula"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 text-sm"
                data-testid="broadcast-tpl-search"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={toggleAll}
                className="h-9 text-xs whitespace-nowrap"
                data-testid="broadcast-tpl-select-all"
              >
                <Checkbox checked={allSel} className="mr-1.5" />
                {allSel ? "Desmarcar" : "Marcar"} visíveis
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
              {filtered.length} de {eligible.length} elegível(is) · <span className="font-semibold text-emerald-700">{selectedIds.size} selecionado(s)</span>
            </p>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500">Nenhum funcionário.</p>
              ) : (
                filtered.map((e) => {
                  const checked = selectedIds.has(e.id);
                  return (
                    <label
                      key={e.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-emerald-50/50" : ""}`}
                      data-testid={`broadcast-tpl-row-${e.matricula}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggle(e.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 truncate">{e.nome}</p>
                        <p className="text-[11px] text-slate-500 font-mono">
                          {e.matricula} · {e.whatsapp}
                        </p>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {!target.video_id && (
              <label className="flex items-center space-x-2 cursor-pointer text-xs text-slate-700 mt-3">
                <input
                  type="checkbox"
                  checked={includeLogo}
                  onChange={(e) => setIncludeLogo(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span>Incluir logo da AVI no envio</span>
              </label>
            )}
          </>
        )}

        {job && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3 py-2"
            data-testid="broadcast-tpl-progress"
          >
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-slate-700">
                {isDone ? "Finalizado" : "Em andamento"}
              </span>
              <Badge variant="outline"
                className={isDone
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"
                  : "bg-amber-100 text-amber-800 border-amber-200 text-[10px]"}>
                {isDone ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {job.sent + job.failed}/{job.total || 0}
              </Badge>
            </div>
            <Progress value={progress} className="h-2.5" />
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 bg-emerald-50 rounded text-center">
                <p className="text-lg font-semibold text-emerald-700">{job.sent}</p>
                <p className="text-[10px] text-emerald-600 uppercase">Recebidas</p>
              </div>
              <div className="p-2 bg-red-50 rounded text-center">
                <p className="text-lg font-semibold text-red-700">{job.failed}</p>
                <p className="text-[10px] text-red-600 uppercase">Falhas</p>
              </div>
            </div>
            {job.errors && job.errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Detalhes ({job.errors.length})
                </summary>
                <ul className="mt-1 max-h-32 overflow-y-auto space-y-1 pl-1">
                  {job.errors.map((e, i) => (
                    <li key={i} className="text-[10px] text-red-600">
                      <span className="font-mono">{e.matricula}</span> {e.nome}: {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </motion.div>
        )}

        <DialogFooter className="gap-2">
          {!job && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                Cancelar
              </Button>
              <Button
                onClick={fire}
                disabled={sending || selectedIds.size === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="broadcast-tpl-send"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Disparar para {selectedIds.size || "0"}
              </Button>
            </>
          )}
          {job && (
            <Button
              onClick={() => onOpenChange(false)}
              disabled={isRunning}
              className={isDone ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid="broadcast-tpl-close"
            >
              {isRunning ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde…</> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Fechar</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminMessageTemplatesTab;
